import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { UserFieldPermission } from '@app/areas/permissions/interfaces/permission';
import { PermissionsService } from '@app/areas/permissions/services/permissions.service';
import { AddPermission } from '@app/_store/app.actions';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { PermissionsModuleGuard } from './permissions-module.guard';


@Injectable({
  providedIn: 'root'
})
export class PermissionsComponentGuard  {

  constructor(
    private permissionService: PermissionsService,
    private router: Router,
    private store: Store
  ) { }

  // The ultimate guard to decide if the user has rights for specific components of not
  // If set properly in app.routes, this guard is executed on every navigation. Propably even multiple times if there are nested components
  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {

    const master: number = route.data.permissionsRequired;
    const requiredLevel: number = route.data.permissionsRequiredLevel;
    const storePermissions: UserFieldPermission[] = this.store.selectSnapshot(store => store.avux.permissions);
    const componentPermission: number = storePermissions?.find(perm => perm.permissionFieldID === master)?.fieldPermission;

    if (master) {

      // Permission is found, but it's 0
      if (componentPermission === 0) {
        return this.permissionDenied(route, state);
      }

      // Permission is found, but it's lower than necessary
      if (requiredLevel && componentPermission < requiredLevel) {
        return this.permissionDenied(route, state);
      }

      // Module permissions does not include navigated component permission
      // As a last change, try to get it with a service
      if (componentPermission === null || componentPermission === undefined) {

        return this.permissionService.getPermission(master).pipe(
          map(response => {

            if (response === 0) {
              return this.permissionDenied(route, state);
            }

            if (requiredLevel && response < requiredLevel) {
              return this.permissionDenied(route, state);
            }

            // Add checked permission to module permissions
            this.store.dispatch(new AddPermission({ permissionFieldID: master, fieldPermission: response }));

            return true;

          }),
          catchError(error => {
            // no permission or error-page
            this.noPermission();
            return of(false);
          })
        );
      }

      // Success
      return true;
    }

    // Let the user trough if no master-setting is set
    return true;
  }

  private permissionDenied(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const reroute: string = typeof route.data.reroute === 'string' ? route.data.reroute.trim() : '';

    if (reroute){
      if(this.tryReroute(reroute, state))
        return true;
      else
        return false;
    }

    this.noPermission();
    return false;
  }

  private tryReroute(reroute: string, state: RouterStateSnapshot): boolean {
    const navigationState = this.router.getCurrentNavigation()?.extras.state || {};
    const existingChain = navigationState[PermissionsModuleGuard.rerouteChainStateKey];
    const rerouteChain: string[] = Array.isArray(existingChain)
      ? existingChain.filter(value => typeof value === 'string')
      : [];
    const currentUrl = this.normalizeUrl(state.url);
    const targetUrl = this.normalizeUrl((rerouteChain.at(0) ?? state.url) + "/" + reroute);

    const createsLoop = targetUrl === currentUrl || rerouteChain.includes(targetUrl);
    const chainTooLong = rerouteChain.length >= PermissionsModuleGuard.maxRerouteChainLength;

    if (createsLoop || chainTooLong) {
      return false;
    }

    void this.router.navigateByUrl(targetUrl, {
      state: {
        ...navigationState,
        [PermissionsModuleGuard.rerouteChainStateKey]: [...rerouteChain, currentUrl]
      }
    });

    return true;
  }

  private normalizeUrl(url: string): string {
    return this.router.parseUrl(url).toString();
  }


  noPermission() {
    this.router.navigate(['no-access']);
  }

}
